/**
 * HTTP server lifecycle for Hono.
 *
 * Thin wrapper over `@hono/node-server`'s `serve()` that keeps a singleton
 * reference so Socket.IO (and graceful shutdown code in `core/server.ts`) can
 * attach to the underlying `http.Server`.
 *
 * NOTE: The full startup pipeline (migrations, seeds, module registration,
 * SPAs, tunnel, Socket.IO) still lives in `src/core/server.ts`. F3 rewires it
 * to build the Hono app via `createApp()` from this layer.
 */

import { createServer as nodeCreateServer } from 'node:http'
import type { AddressInfo } from 'node:net'
import type { Server as HttpServer } from 'node:http'
import { serve, type ServerType } from '@hono/node-server'
import type { HonoApp } from './types.js'

let activeServer: ServerType | null = null

export interface StartHttpServerOptions {
  port: number
  hostname?: string
  /**
   * Called once the server is listening.
   * For port:0 callers, this is the only way to learn the real assigned port.
   */
  onListen?: (info: { port: number; hostname: string }) => void
  /**
   * Pre-created http.Server to reuse (for Vite HMR sharing).
   * When provided, the server's request listener must already be attached —
   * startHttpServer will only call .listen() on it.
   */
  existingServer?: HttpServer
}

/**
 * Create a raw http.Server with no request listeners.
 * Used to share the server with Vite HMR before the Hono app is ready.
 */
export function createRawHttpServer(): HttpServer {
  return nodeCreateServer()
}

/**
 * Bind a Hono app to a node `http.Server` and start listening.
 * Returns the underlying server so callers (Socket.IO, tests) can attach to it.
 *
 * When `options.existingServer` is provided (pre-created for Vite HMR sharing),
 * the server's Hono request listener must already be attached by the caller.
 * This function only calls `.listen()` on the existing server.
 * Node.js allows re-calling `.listen()` after `.close()`, so EADDRINUSE retries work.
 */
export function startHttpServer(app: HonoApp, options: StartHttpServerOptions): ServerType {
  if (activeServer) {
    throw new Error('HTTP server is already running. Call stopHttpServer() first.')
  }
  const listeningListener = options.onListen
    ? (info: AddressInfo) => options.onListen!({ port: info.port, hostname: info.address })
    : undefined

  if (options.existingServer) {
    const srv = options.existingServer
    srv.listen(options.port, options.hostname, () => {
      const addr = srv.address()
      if (listeningListener && addr && typeof addr === 'object') {
        listeningListener({ port: addr.port, address: addr.address } as AddressInfo)
      }
    })
    activeServer = srv as unknown as ServerType
  } else {
    activeServer = serve(
      {
        fetch: app.fetch,
        port: options.port,
        ...(options.hostname ? { hostname: options.hostname } : {})
      },
      listeningListener
    )
  }
  return activeServer
}

/**
 * Return the running `http.Server`, or null if not started.
 * Exposed so Socket.IO and shutdown hooks can reach it.
 */
export function getHttpServer(): HttpServer | null {
  // `ServerType` is a union of http.Server / http2.Http2SecureServer. For our
  // current usage (Node HTTP + Socket.IO) it's always the http.Server variant.
  return (activeServer as unknown as HttpServer | null) ?? null
}

/**
 * Close the running HTTP server. Resolves when all connections drain.
 *
 * Calls `closeIdleConnections()` to immediately terminate keep-alive sockets
 * that have no active request, so `close()` doesn't hang waiting for Node's
 * keep-alive timeout (default ~5s). Active requests are left to drain.
 *
 * LIMITATION: if `stop()` is invoked outside the signal handler (e.g. via
 * programmatic restart), requests in-flight longer than the caller's timeout
 * may still be cut when the event loop exits. The signal-driven path has its
 * own force-exit timer that escalates to `process.exit(1)` after 10s.
 *
 * TODO: add grace-period escalation to `closeAllConnections()` as a fallback.
 */
export async function stopHttpServer(): Promise<void> {
  if (!activeServer) return
  const httpSrv = getHttpServer()
  if (httpSrv && typeof httpSrv.closeIdleConnections === 'function') {
    httpSrv.closeIdleConnections()
  }
  try {
    await new Promise<void>((resolve, reject) => {
      activeServer!.close((err) => {
        // ERR_SERVER_NOT_RUNNING means serve() never reached listening
        // (e.g. EADDRINUSE during startup) — treat as success so retries work.
        if (err && (err as NodeJS.ErrnoException).code === 'ERR_SERVER_NOT_RUNNING') resolve()
        else if (err) reject(err)
        else resolve()
      })
    })
  } finally {
    activeServer = null
  }
}
